import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { checkReadyToBid } from '@/components/GridItemCard/Card/helpers/checkReadyToBid';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderId, getUserIsInGoodStanding } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { fetchBidIncrementsIfNeeded } from '@/redux/modules/bidIncrements/bidIncrements.actions';
import { getBidIncrementsByItemId } from '@/redux/modules/bidIncrements/bidIncrements.selectors';
import { fetchBidLimitBalanceIfNeeded } from '@/redux/modules/bidLimit/bidLimit.actions';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getCatalogNetworkStatus,
    getLatestMessageTimestamp,
    getLiveCatalogClosed,
    getLiveCatalogCurrentItemId,
    getLiveCatalogStatus,
    getLiveCatalogWon,
    getLiveItemDataCurrentAskPrice,
    getLiveItemDataLeadingBidder,
} from '@/redux/modules/console';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryBidderMaxBid,
    getItemSummaryCatalogId,
    getItemSummaryCurrency,
    getItemSummaryIsTimedPlusAuction,
    getItemSummaryLeadingBid,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchItemDetailsIfNeeded } from '@/redux/modules/itemDetail';
import { fetchItemFacetsIfNeeded } from '@/redux/modules/itemFacets';
import { getBidderSubmittedBidId, submitLiveBid } from '@/redux/modules/liveBid';
import { CompleteAccountModalContext, ModalContext, openPlaceAbsenteeBidModal } from '@/redux/modules/modal';
import { getNextBidMinimum } from '@/utils/absenteeBids';
import { TrackPlaceBidPressedButtonLocation, useTrackPlaceBidPressedAnalytics } from '@/utils/amplitude/analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

export type ItemCardLiveBidButtonProps = {
    itemId: number;
};

export const ItemCardLiveBidButton = ({ itemId }: ItemCardLiveBidButtonProps) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    const accountInGoodStanding = useAppSelector(getUserIsInGoodStanding);

    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const isTimedPlusAuction = useAppSelector((state) => getItemSummaryIsTimedPlusAuction(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));

    const { approved, bidLimit, blockedByAuctioneer, declined, limited, suspended } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    const bidIncrements = useAppSelector((state) => getBidIncrementsByItemId(state, itemId));

    const closed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const won = useAppSelector((state) => getLiveCatalogWon(state, catalogId));
    const status = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const liveUpdateTimestamp = useAppSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const currentPrice = leadingBid || salePrice || startPrice || 0;
    const overBidLimit = limited ? bidLimitBalance + currentPrice > bidLimit : false;
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));
    const bidderId = useAppSelector(getBidderId);
    const leadingBidderId = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const location = useLocation();
    const trackPlaceBidButtonPressedAnalytics = useTrackPlaceBidPressedAnalytics(catalogId, itemId);

    const leadingBidder = leadingBidderId === bidderId;

    const [bidAmount, setBidAmount] = useState(
        getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0
    );

    const newBidMinimum = getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0;

    const submitted = useAppSelector(getBidderSubmittedBidId) === itemId;

    const isStoppedFromBidding =
        !accountInGoodStanding ||
        (!approved && !limited) ||
        suspended ||
        declined ||
        blockedByAuctioneer ||
        overBidLimit ||
        !checkReadyToBid({
            catalogId,
            closed,
            currentAskPrice,
            currentItem,
            itemId,
            liveUpdateTimestamp,
            networkStatus,
            status,
            won,
        });

    const handlePlaceBid = async () => {
        if (isTimedPlusAuction) {
            await placeAbsenteeBid();
        } else {
            placeBid();
        }
        trackPlaceBidButtonPressedAnalytics(TrackPlaceBidPressedButtonLocation.ItemCard);
    };

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.placeBid,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.PlaceBid,
        }),
        [itemId]
    );

    const placeAbsenteeBid = async () => {
        const actionPromises: Promise<any>[] = [
            dispatch(fetchItemDetailsIfNeeded([itemId])),
            dispatch(fetchItemFacetsIfNeeded([itemId])),
            dispatch(fetchBidIncrementsIfNeeded(catalogId)),
            dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] })),
        ];
        await Promise.all(actionPromises);
        dispatch(
            openPlaceAbsenteeBidModal({
                bidAmount,
                isInstantAutopay,
                itemId,
                modalContext,
            })
        );
    };

    const placeBid = () => {
        // don't only rely on the "disabled" button to prevent bidders who haven't been approved
        if ((approved || limited) && !isStoppedFromBidding) {
            dispatch(submitLiveBid(Date.now(), catalogId, currentAskPrice, currency, itemId, location));
        } else {
            return;
        }
    };

    useEffect(() => {
        setBidAmount(newBidMinimum);
    }, [bidIncrements, newBidMinimum]);

    const [hasLoadedBalance, setHasLoadedBalance] = useState(false);
    useEffect(() => {
        if (!hasLoadedBalance) {
            dispatch(
                fetchBidLimitBalanceIfNeeded({
                    catalogId,
                    itemId,
                })
            );
            setHasLoadedBalance(true);
        }
    }, [catalogId, dispatch, hasLoadedBalance, itemId]);

    const [hasLoadedIncrements, setHasLoadedIncrements] = useState(false);
    useEffect(() => {
        if (!hasLoadedIncrements) {
            dispatch(fetchBidIncrementsIfNeeded(catalogId));
            setHasLoadedIncrements(true);
        }
    }, [catalogId, dispatch, hasLoadedIncrements]);

    if (!hasLoadedBalance || !hasLoadedIncrements) {
        return null;
    }

    if (leadingBidder && !isTimedPlusAuction) {
        return (
            <Button
                className="w-full !cursor-default !border-border-success !bg-background-success-dark"
                size="sm"
            >
                {formatMessage(['bidding_console', 'console', 'button', 'winning'])}
            </Button>
        );
    }

    return (
        <Button
            className="w-full"
            disabled={submitted}
            loading={submitted}
            onClick={handlePlaceBid}
            size="sm"
            style="critical"
        >
            {isTimedPlusAuction &&
                bidderHasBid &&
                formatMessage(['increase_bid'], {
                    values: {
                        amount: (
                            <Fragment key="item-card-live-bid-button-amount">
                                <FormattedCurrency
                                    className="ml-2xs"
                                    noFraction
                                    sourceCurrency={currency}
                                    value={currentAskPrice || bidAmount}
                                />
                            </Fragment>
                        ),
                    },
                })}
            {isTimedPlusAuction &&
                !bidderHasBid &&
                formatMessage(['catalogPage', 'placeBid'], {
                    values: {
                        amount: (
                            <Fragment key="item-card-live-bid-button-amount">
                                <FormattedCurrency
                                    className="ml-2xs"
                                    noFraction
                                    sourceCurrency={currency}
                                    value={currentAskPrice || bidAmount}
                                />
                            </Fragment>
                        ),
                    },
                })}
            {!isTimedPlusAuction &&
                formatMessage(['bidding_console', 'console', 'button', 'bid'], {
                    values: {
                        amount: (
                            <Fragment key="item-card-live-bid-button-amount">
                                <FormattedCurrency
                                    className="ml-2xs"
                                    noFraction
                                    sourceCurrency={currency}
                                    value={currentAskPrice || bidAmount}
                                />
                            </Fragment>
                        ),
                    },
                })}
        </Button>
    );
};
